/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import type { MouseEventHandler } from 'react';

const MAX_POINTER_MOVE_PX = 3;
const NON_FOCUSABLE_TARGET_SELECTOR = [
  'textarea',
  'button',
  'a',
  'input',
  'select',
  '[role="button"]',
  '.ndl-ai-prompt-context-tag',
  '.ndl-ai-file-tag',
  '.ndl-ai-image-tag',
].join(',');

/**
 * Focuses the prompt textarea when users click the non-interactive wrapper.
 *
 * This keeps keyboard input in sync with the visible focused state.
 */
export const focusTextareaFromPromptWrapper: MouseEventHandler<
  HTMLDivElement
> = (event) => {
  if (event.button !== 0) {
    return;
  }

  const container = event.currentTarget;
  const target = event.target instanceof HTMLElement ? event.target : null;
  const textarea = container.querySelector('textarea');

  if (textarea === null) {
    return;
  }

  // Do not override focus/behavior when users click interactive controls.
  if (target?.closest(NON_FOCUSABLE_TARGET_SELECTOR)) {
    return;
  }

  let didMove = false;
  const startX = event.clientX;
  const startY = event.clientY;

  const cleanup = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (moveEvent: MouseEvent) => {
    if (
      Math.abs(moveEvent.clientX - startX) > MAX_POINTER_MOVE_PX ||
      Math.abs(moveEvent.clientY - startY) > MAX_POINTER_MOVE_PX
    ) {
      didMove = true;
    }
  };

  const handleMouseUp = (upEvent: MouseEvent) => {
    cleanup();

    if (didMove) {
      return;
    }

    const upTarget =
      upEvent.target instanceof HTMLElement ? upEvent.target : null;

    if (
      upTarget === null ||
      !container.contains(upTarget) ||
      upTarget.closest(NON_FOCUSABLE_TARGET_SELECTOR)
    ) {
      return;
    }

    textarea.focus();
  };

  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
};
